import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { onAuthStateChanged, signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendEmailVerification, updateProfile, signOut } from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Ensure a cms_users profile doc exists for this Firebase Auth user.
// Idempotent: backend creates the doc if missing, otherwise just refreshes
// emailVerified / groupId. Safe to call on every login.
const ensureProfile = async (user) => {
  try {
    const token = await user.getIdToken();
    const apiURL = process.env.REACT_APP_API_URL || '/api';
    await axios.post(`${apiURL}/auth/signup`, { alias: user.displayName || null }, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (err) {
    // Non-fatal — permission check below will still gate access.
    console.warn('ensureProfile failed:', err?.response?.data || err.message);
  }
};

// Check if user has CMS access via backend API
const checkCMSAccess = async (user) => {
  try {
    // Get the user's ID token
    const token = await user.getIdToken();

    // Call backend API to check permissions using the same API URL as the rest of the app
    const apiURL = process.env.REACT_APP_API_URL || '/api';
    const response = await axios.get(`${apiURL}/cms/permissions`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    const perms = response.data?.permissions;
    if (perms && typeof perms === 'object') {
      // Must have at least one route assigned, or be a client mode user
      const hasRoutes = Array.isArray(perms.routes) && perms.routes.length > 0;
      const isClientMode = perms.clientMode === true;
      if (hasRoutes || isClientMode) {
        return { hasAccess: true, permissions: perms, reason: null };
      }
    }

    return { hasAccess: false, permissions: null, reason: 'no_permissions' };
  } catch (error) {
    console.error('Error checking CMS access:', error);
    console.error('Response:', error.response?.data);
    console.error('Status:', error.response?.status);

    // Propagate specific backend reason codes (e.g. email_not_verified) so
    // callers can surface the right message. Fall back to generic denial.
    const code = error.response?.data?.code;
    if (code === 'email_not_verified') {
      return { hasAccess: false, permissions: null, reason: 'email_not_verified' };
    }
    return { hasAccess: false, permissions: null, reason: 'denied' };
  }
};

const accessDeniedMessage = (reason, email) => {
  if (reason === 'email_not_verified') {
    return `Email not verified. Please check ${email} for the verification link before signing in.`;
  }
  return `Access denied. The email ${email} does not have permissions to access this system. Please contact an administrator.`;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Set while the signup flow is running so the onAuthStateChanged listener
  // doesn't mistake the transient signed-in state for a real login and
  // surface a misleading "Access denied" error.
  const signingUpRef = useRef(false);

  // Login with Google
  const loginWithGoogle = async () => {
    try {
      setError(null);

      // Use popup flow (works for both localhost and production)
      console.log('🔑 Starting Google sign-in with popup...');
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      console.log('✅ Google sign-in successful for:', user.email);

      await ensureProfile(user);

      // Check CMS access via backend API
      console.log('🔍 Checking CMS access...');
      const { hasAccess, permissions: userPermissions, reason } = await checkCMSAccess(user);

      if (!hasAccess) {
        console.error('❌ CMS access denied for user:', user.email, 'reason:', reason);
        const msg = accessDeniedMessage(reason, user.email);
        await signOut(auth);
        throw new Error(msg);
      }

      console.log('✅ CMS access granted for user:', user.email);
      console.log('📋 Permissions:', userPermissions);
      setPermissions(userPermissions);
      return result;
    } catch (error) {
      console.error('❌ Login error:', error);
      // Handle specific error cases
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled.');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Pop-up blocked. Please allow pop-ups for this site.');
      } else if (error.message.includes('Access denied') || error.message.includes('Email not verified')) {
        setError(error.message);
      } else {
        setError('Failed to sign in. Please try again.');
      }
      throw error;
    }
  };

  // Sign up with email and password. Creates the Firebase Auth user, sends
  // the verification email (Firebase default SMTP for now — SendGrid later),
  // provisions the cms_users profile doc, then signs out so the user lands
  // back on the login screen. New accounts start with no CMS permissions
  // and must be granted access by an admin via the CMS.
  const signup = async (email, password, displayName) => {
    signingUpRef.current = true;
    try {
      setError(null);
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const user = result.user;

      if (displayName) {
        try { await updateProfile(user, { displayName }); } catch (_) { /* non-fatal */ }
      }

      try { await sendEmailVerification(user); } catch (_) { /* non-fatal — backend will retry */ }

      const token = await user.getIdToken();
      const apiURL = process.env.REACT_APP_API_URL || '/api';
      try {
        await axios.post(`${apiURL}/auth/signup`, { alias: displayName || null }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (err) {
        console.error('Failed to provision cms_users profile:', err);
      }

      await signOut(auth);
      // Keep the flag up for one more tick so the auth-state listener's
      // async access check (triggered by the transient sign-in above) can
      // observe it and skip setting a spurious "Access denied" error.
      setTimeout(() => { signingUpRef.current = false; }, 500);
      return { email: user.email };
    } catch (error) {
      signingUpRef.current = false;
      if (error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password must be at least 6 characters.');
      } else {
        setError('Failed to create account. Please try again.');
      }
      throw error;
    }
  };

  // Login with email and password
  const loginWithEmail = async (email, password) => {
    try {
      setError(null);
      const result = await signInWithEmailAndPassword(auth, email, password);
      const user = result.user;

      await ensureProfile(user);

      const { hasAccess, permissions: userPermissions, reason } = await checkCMSAccess(user);
      if (!hasAccess) {
        const msg = accessDeniedMessage(reason, user.email);
        await signOut(auth);
        throw new Error(msg);
      }

      setPermissions(userPermissions);
      return result;
    } catch (error) {
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
        setError('Invalid email or password.');
      } else if (error.message.includes('Access denied') || error.message.includes('Email not verified')) {
        setError(error.message);
      } else {
        setError('Failed to sign in. Please try again.');
      }
      throw error;
    }
  };

  // Logout
  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);
      setPermissions(null);
      setCurrentUser(null);
    } catch (error) {
      setError('Failed to log out.');
      throw error;
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    // Check if we're using emulators or production
    const useEmulators = process.env.REACT_APP_USE_EMULATORS === 'true';
    const apiURL = process.env.REACT_APP_API_URL || '/api';
    const isLocalAPI = apiURL.includes('localhost') || apiURL.includes('127.0.0.1');

    // Only bypass auth if we're on localhost AND using local API or emulators
    if (window.location.hostname === 'localhost' && (isLocalAPI || useEmulators)) {
      console.log('🔧 Development mode: bypassing authentication (local API)');
      setCurrentUser({
        email: 'dev@localhost',
        displayName: 'Dev User',
        uid: 'dev-user-local'
      });
      setPermissions({
        routes: ["/", "/dashboard", "/insights", "/cms"],
        functions: [],
        companies: [],
        dev: true,
        CMS_access: true,
      });
      setLoading(false);
      return;
    }

    // If on localhost but using production API, require real authentication
    if (window.location.hostname === 'localhost' && !isLocalAPI) {
      console.log('🔧 Development mode with production API: authentication required');
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Ignore transient sign-in during the signup flow — we're about to
        // sign the user back out, and they don't have CMS access yet by design.
        if (signingUpRef.current) {
          setLoading(false);
          return;
        }
        console.log('🔐 User authenticated:', user.email);
        // Check CMS access via backend API
        const { hasAccess, permissions: userPermissions, reason } = await checkCMSAccess(user);

        if (!hasAccess) {
          console.error('❌ No CMS access detected, signing out:', user.email, 'reason:', reason);
          const msg = accessDeniedMessage(reason, user.email);
          await signOut(auth);
          setCurrentUser(null);
          setPermissions(null);
          setError(msg);
        } else {
          console.log('✅ CMS access granted, user permissions:', userPermissions);
          setCurrentUser(user);
          setPermissions(userPermissions);
          setError(null);
        }
      } else {
        console.log('🔓 No user authenticated');
        setCurrentUser(null);
        setPermissions(null);
        // Don't clear error here — it may be an "Access denied" message we want to show on the login page.
        // Error is cleared explicitly in logout() and at the start of loginWithGoogle().
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    permissions,
    loading,
    error,
    loginWithGoogle,
    loginWithEmail,
    signup,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
